import React from "react";
import type {AgentMetadata, ChatMessage} from "~/pages/chat-agent/types/chatTypes";
import {CollapsibleSection} from "~/pages/chat-agent/components/AgentActivity/CollapsibleSection";
import {ToolCallRow, type ToolCallStatus} from "~/pages/chat-agent/components/AgentActivity/ToolCallRow";
import {toolCallKey} from "~/pages/chat-agent/utils/chatBlocks";
import {agentLabel, toolLabel} from "~/pages/chat-agent/utils/toolLabels";

type Props = {
    calls: ChatMessage[];
    /** The turn is still streaming: unanswered calls are running. */
    isActive: boolean;
    /** The turn already sent text after these calls: the section folds to make room for it. */
    followedByText: boolean;
    /** Keys (toolCallKey) of the calls that handed the turn to another agent. */
    handoffs: ReadonlySet<string>;
    /** From agents-metadata, for display names. */
    agents?: AgentMetadata[];
};

function statusOf(call: ChatMessage, isActive: boolean, isHandoff: boolean): ToolCallStatus {
    // A handoff stays unanswered while the agent it called works; that agent's own steps show its progress.
    if (call.event_type === "function_response" || isHandoff) return "done";
    return isActive ? "running" : "no_response";
}

/** A run of consecutive tool calls in an agent turn, collapsed into one section. */
export const ToolCallsSection: React.FC<Props> = ({calls, isActive, followedByText, handoffs, agents}) => {
    const running = isActive
        ? calls.findLast(c => c.event_type === "function_call" && !handoffs.has(toolCallKey(c)))
        : undefined;
    const title = running
        ? `Running ${toolLabel(running.content)}…`
        : `Used ${calls.length} ${calls.length === 1 ? "tool" : "tools"}`;

    return (
        <CollapsibleSection
            title={title}
            isActive={isActive}
            isWorking={running != null}
            scrollKey={calls.length}
            autoOpen={isActive && !followedByText}
        >
            {calls.map(call => {
                const isHandoff = handoffs.has(toolCallKey(call));
                return (
                    <ToolCallRow
                        key={toolCallKey(call)}
                        call={call}
                        status={statusOf(call, isActive, isHandoff)}
                        label={isHandoff ? `Handed off to ${agentLabel(call.content, agents)}` : toolLabel(call.content)}
                    />
                );
            })}
        </CollapsibleSection>
    );
};
